import {
  MetricsQueryParams,
  QueryIface,
  QueryResultsEntry,
  RawQueryResultsEntry
} from '@kentik/ui-shared/types/queries';
import DeviceModel from 'app/stores/device/DeviceModel';
import $metrics from 'app/stores/metrics/$metrics';

export enum FlowMeasurement {
  FLOW = '/kentik/licensing/flow',
  FLOW_PROXY = '/kentik/licensing/flow/flowproxy',
  KPROXY = '/kentik/licensing/flow/kproxy'
}

/**
 * Strategy that drives both the measurement set and the aggregation mode for plan-level flow queries.
 *   - HIGHEST_FPS (DevicePak): query all available received measurements grouped per device, then attribute
 *     each device to the measurement with the highest received-flow value.
 *   - SAAS_ONLY (FlowPak): query only the FLOW measurement, grouped by plan and merged across devices so the
 *     backend returns a single device-summed plan series.
 */
export enum PlanFlowSelectionStrategy {
  HIGHEST_FPS = 'highestFps',
  SAAS_ONLY = 'saasOnly'
}

/**
 *   1 day  → 60s   (1 minute bins)
 *   7 days → 600s  (10 minute bins)
 *   30 days → 10800s (3 hour bins)
 */
const LOOKBACK_WINDOW_SIZE: Record<string, number> = {
  PT86400S: 60,
  PT604800S: 600,
  PT2592000S: 10800
};

const DEFAULT_WINDOW_SIZE = 60;

export function getWindowSizeForLookback(lookback?: string): number {
  return (lookback && LOOKBACK_WINDOW_SIZE[lookback]) || DEFAULT_WINDOW_SIZE;
}

export function getAvailableReceivedMeasurements(): string[] {
  const measurements: string[] = [FlowMeasurement.FLOW];

  if ($metrics.measurementModelByName(FlowMeasurement.FLOW_PROXY)) {
    measurements.push(FlowMeasurement.FLOW_PROXY);
  }

  if ($metrics.measurementModelByName(FlowMeasurement.KPROXY)) {
    measurements.push(FlowMeasurement.KPROXY);
  }

  return measurements;
}

export function getDeviceFlowMeasurement(device: DeviceModel): string {
  if (!device || !device.get('stats')) {
    console.warn('Device or device stats not defined, defaulting to /flow query measurement');
  }

  let measurement = FlowMeasurement.FLOW; // Default to /flow measurement

  const IsAgent = (device.get('stats') as { IsAgent: number } | undefined)?.IsAgent;

  // If IsAgent is defined and greater than 0, use flowproxy/kproxy measurements
  if (!!IsAgent && IsAgent > 0) {
    if (device.get('device_kentik_agent_id') && $metrics.measurementModelByName(FlowMeasurement.FLOW_PROXY)) {
      measurement = FlowMeasurement.FLOW_PROXY;
    } else if (!device.get('device_kentik_agent_id') && $metrics.measurementModelByName(FlowMeasurement.KPROXY)) {
      measurement = FlowMeasurement.KPROXY;
    }
  }

  return measurement;
}

export function getFlowQueries({
  dimensions,
  filters = [],
  limit = 100,
  lookback,
  metric,
  measurement,
  merge,
  rollup = 'avg',
  timeseriesLimit = 20
}: MetricsQueryParams): QueryIface {
  const query: QueryIface = {
    update_frequency: 0,
    use_kmetrics: true,
    show_overlay: false,
    show_total_overlay: false,
    kmetrics: {
      measurement,
      dimensions,
      metrics: [{ name: metric, type: 'gauge' }],
      range: { lookback },
      window: {
        size: getWindowSizeForLookback(lookback),
        fn: {
          [`${metric}`]: 'max'
        }
      },
      rollups: {
        [`${rollup}_${metric}`]: { metric, aggregate: `${rollup}` }
      },
      sort: [{ direction: 'desc', name: `${rollup}_${metric}` }],
      limit,
      includeTimeseries: timeseriesLimit,
      streamingUpdate: false,
      filters: {
        connector: 'All',
        filterGroups: [
          {
            name: '',
            named: false,
            connector: 'All',
            not: false,
            filters
          }
        ]
      }
    }
  };

  if (merge) {
    query.kmetrics.merge = merge;
  }
  return query;
}

/**
 * Deduplicates received flow results across measurement types by keeping, for each device, only the
 * measurement that reported the highest received-flow value (`${rollup}_${metric}`).
 *
 * A device that appears in multiple measurements (FLOW, FLOW_PROXY, KPROXY) is attributed to the single
 * source with the largest value, preventing the same device's flow from being counted more than once.
 * On a tie, the first measurement encountered (object key order) wins.
 */
export function deduplicateReceivedFlowResults(
  resultsByMeasurement: Record<string, RawQueryResultsEntry>,
  { rollup = 'p95', metric = 'in' }: { rollup?: string; metric?: string } = {}
): Record<string, QueryResultsEntry | null> {
  const rollupKey = `${rollup}_${metric}`;

  // Normalize all entries upfront to plain objects with explicit models/size fields so the return type is
  // uniform across all measurements. QueryResultsCollection.size is a @computed prototype getter and is
  // not copied by object spread; models is a MobX observable array reference. Normalizing here ensures
  // every entry exposes a plain array for models and a plain number for size regardless of input type.
  const normalizedEntries = Object.entries(resultsByMeasurement).map(([key, value]) => {
    const val = value && value.models ? { models: Array.from(value.models), size: value.size } : null;

    return [key, val];
  });

  const normalizedResults: Record<string, QueryResultsEntry | null> = Object.fromEntries(normalizedEntries);

  // For each device, determine which measurement reported the highest received-flow value.
  const winningMeasurementByDid = new Map<unknown, string>();
  const highestValueByDid = new Map<unknown, number>();

  Object.entries(normalizedResults).forEach(([measurement, entry]) => {
    entry?.models.forEach((model) => {
      const did = model.get('did');
      const value = (model.get(rollupKey) as number) || 0;
      const currentHighest = highestValueByDid.get(did);

      if (currentHighest === undefined || value > currentHighest) {
        highestValueByDid.set(did, value);
        winningMeasurementByDid.set(did, measurement);
      }
    });
  });

  // Keep only the model whose measurement won for each device.
  return Object.fromEntries(
    Object.entries(normalizedResults).map(([measurement, entry]) => {
      if (entry == null) {
        return [measurement, null];
      }

      const filteredModels = entry.models.filter(
        (model) => winningMeasurementByDid.get(model.get('did')) === measurement
      );

      return [measurement, { ...entry, models: filteredModels, size: filteredModels.length }];
    })
  );
}

/**
 * Returns true if a flow source has at least one model in its result set.
 * Use this to determine whether a query returned any data at all.
 */
export function sourceHasData(source: QueryResultsEntry | null | undefined): boolean {
  return !!source?.models?.length;
}
